// ARIA state for a search field that keeps focus while the arrow keys move a
// highlight through its results (the WAI-ARIA combobox pattern with a
// listbox popup). Focus never reaches the results, so a screen reader learns
// which one is highlighted only through the field's aria-activedescendant.

const ANNOUNCE_DELAY = 150;

// Makes `option` an option of `listbox`, with an id unique in the document.
export function prepareOption(listbox, option) {
  option.setAttribute("role", "option");
  if (option.id) return;

  let n = 0;
  let id;
  do {
    id = `${listbox.id}-option-${n++}`;
  } while (document.getElementById(id));
  option.id = id;
}

// Highlights `option` (or nothing, for null) among `options`: the active
// class for the eye, aria-selected and the field's aria-activedescendant for
// a screen reader.
export function highlight(field, options, option, { scroll = false } = {}) {
  options.forEach((each) => {
    const on = each === option;
    each.classList.toggle("active", on);
    if (on) each.setAttribute("aria-selected", "true");
    else each.removeAttribute("aria-selected");
  });

  if (option) {
    field.setAttribute("aria-activedescendant", option.id);
    if (scroll) option.scrollIntoView?.({ block: "nearest" });
  } else {
    field.removeAttribute("aria-activedescendant");
  }
}

export function countText(count) {
  if (count === 0) return "No results";
  return count === 1 ? "1 result" : `${count} results`;
}

// Writes to a polite live region after a short pause, so typing a query
// announces only the count it settles on, and a region that has just been
// shown is in the accessibility tree before its text changes.
export class Announcer {
  #region;
  #timer = null;

  constructor(region) {
    this.#region = region;
  }

  announce(text) {
    if (!this.#region) return;
    this.#cancel();
    this.#timer = setTimeout(() => {
      this.#timer = null;
      this.#region.textContent = text;
    }, ANNOUNCE_DELAY);
  }

  clear() {
    this.#cancel();
    if (this.#region) this.#region.textContent = "";
  }

  #cancel() {
    if (this.#timer === null) return;
    clearTimeout(this.#timer);
    this.#timer = null;
  }
}
